/**
 * Offline-first sync engine for survey captures.
 *
 * Mirrors the existing SyncEngine pattern but handles survey-specific items:
 * photos (sign → PUT → commit), findings, tracer steps, documents, and
 * transcripts. Items are queued in surveyStore and drained when online.
 */

import { surveyStore, type SyncQueueItem, type SyncAction } from '../offline/surveyStore'
import { uploadEvidence } from './evidence'
import { addFinding, addDocument, addTracerStep } from './survey'
import { isWhisperFallbackEnabled, transcribeWithWhisper } from './voiceTranscript'
import { ApiRequestError } from '../errors'
import { safeLogger } from '../logger'
import { assertDecodableImage } from '../media/validateImage'

// ── Error types ─────────────────────────────────────────────────────────────

/** Thrown when a sync item is permanently unrecoverable (e.g. lost blob data). */
export class NonRetryableSyncError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NonRetryableSyncError'
  }
}

// ── Config ──────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000
const MAX_RETRIES = 5

// ── Queue helpers ───────────────────────────────────────────────────────────

export function buildQueueItem(
  action: SyncAction,
  visitId: string,
  surveyId: string,
  payload: Record<string, unknown>,
): SyncQueueItem {
  return {
    id: crypto.randomUUID(),
    action,
    payload,
    visit_id: visitId,
    survey_id: surveyId,
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
  }
}

function requestImmediateSync(): void {
  if (typeof window === 'undefined') return
  if (!navigator.onLine) return
  queueMicrotask(() => {
    void surveySyncEngine.sync()
  })
}

export async function enqueuePhoto(
  visitId: string,
  surveyId: string,
  photoId: string,
): Promise<void> {
  await surveyStore.enqueue(
    buildQueueItem('upload_photo', visitId, surveyId, { photo_id: photoId }),
  )
  requestImmediateSync()
}

export async function enqueueFinding(
  visitId: string,
  surveyId: string,
  findingId: string,
): Promise<void> {
  await surveyStore.enqueue(
    buildQueueItem('submit_finding', visitId, surveyId, { finding_id: findingId }),
  )
  requestImmediateSync()
}

export async function enqueueDocument(
  visitId: string,
  surveyId: string,
  documentId: string,
): Promise<void> {
  await surveyStore.enqueue(
    buildQueueItem('submit_document', visitId, surveyId, { document_id: documentId }),
  )
  requestImmediateSync()
}

export async function enqueueTranscript(
  visitId: string,
  surveyId: string,
  transcriptId: string,
): Promise<void> {
  await surveyStore.enqueue(
    buildQueueItem('submit_transcript', visitId, surveyId, { transcript_id: transcriptId }),
  )
  requestImmediateSync()
}

// ── Item processors ─────────────────────────────────────────────────────────

export async function uploadPendingPhotoEvidence(
  visitId: string,
  photo: { photo_id: string; blob?: Blob; mime_type: string },
): Promise<void> {
  // Guard: reject blobs that are too small to be a real image.
  // A sub-100-byte "image" is a corrupted capture — retrying it will only
  // create orphan evidence_items rows on the backend.
  if (!photo.blob || photo.blob.size < 100) {
    safeLogger.logError('survey_sync_photo_corrupt', photo.photo_id)
    throw new NonRetryableSyncError('Photo data lost — recapture required')
  }

  try {
    await assertDecodableImage(photo.blob)
  } catch (error) {
    safeLogger.logError('survey_sync_photo_decode_failed', photo.photo_id)
    throw new NonRetryableSyncError(
      error instanceof Error ? error.message : 'Photo appears corrupted — recapture required',
    )
  }

  const file = new File([photo.blob], `${photo.photo_id}.jpg`, { type: photo.mime_type })
  await uploadEvidence(visitId, file, { evidenceId: photo.photo_id })
}

async function processPhoto(item: SyncQueueItem): Promise<void> {
  const photoId = String(item.payload.photo_id)
  const photo = await surveyStore.getPhoto(photoId)
  if (!photo) {
    // Photo was removed locally — discard queue item
    return
  }

  await uploadPendingPhotoEvidence(item.visit_id, photo)
  // Keep the local photo after upload so walkthrough thumbnails and later
  // finding submission can still reference the captured evidence while the
  // survey remains in progress. Survey cleanup removes photos at completion.
}

async function processFinding(item: SyncQueueItem): Promise<void> {
  const findingId = String(item.payload.finding_id)
  const finding = await surveyStore.getFinding(findingId)
  if (!finding) return

  await addFinding(item.visit_id, item.survey_id, {
    domain: finding.domain,
    standard_ref: finding.standard_ref,
    severity: finding.severity,
    observation: finding.observation,
    area: finding.area,
    photo_ids: finding.photo_ids,
    audio_transcript: finding.audio_transcript,
  })
  await surveyStore.removeFinding(findingId)
}

async function processDocument(item: SyncQueueItem): Promise<void> {
  const documentId = String(item.payload.document_id)
  const doc = await surveyStore.getDocument(documentId)
  if (!doc) return

  // Upload the document blob as evidence first, if present
  let evidencePath: string | null = null
  if (doc.blob) {
    const file = new File([doc.blob], `${documentId}.pdf`, { type: doc.mime_type || 'application/pdf' })
    const result = await uploadEvidence(item.visit_id, file)
    evidencePath = result.path
  }

  await addDocument(item.visit_id, item.survey_id, {
    doc_type: doc.doc_type,
    label: doc.label,
    evidence_path: evidencePath,
    area: doc.area,
  })
  await surveyStore.removeDocument(documentId)
}

async function processTranscript(item: SyncQueueItem): Promise<void> {
  const transcriptId = String(item.payload.transcript_id)
  const transcript = await surveyStore.getTranscript(transcriptId)
  if (!transcript) return

  // Optional Whisper re-transcribe only when the edge path is explicitly
  // enabled (#1804). Default OFF — no transcribe function is deployed, so
  // invoking would 404/503 and waste offline-queue retries.
  if (
    isWhisperFallbackEnabled() &&
    transcript.audio_blob &&
    transcript.source === 'web_speech'
  ) {
    try {
      const whisperResult = await transcribeWithWhisper(transcript.audio_blob)
      transcript.text = whisperResult.text
    } catch {
      // Keep the web_speech text as-is; never block transcript sync on Whisper.
    }
  }

  // Transcript text is attached to findings or tracer steps elsewhere;
  // clean up the local copy once synced
  await surveyStore.removeTranscript(transcriptId)
}

const PROCESSORS: Record<SyncAction, (item: SyncQueueItem) => Promise<void>> = {
  upload_photo: processPhoto,
  submit_finding: processFinding,
  submit_document: processDocument,
  submit_transcript: processTranscript,
  submit_tracer_step: async (item) => {
    // Tracer steps are submitted inline during capture; this handles
    // offline-queued steps
    const payload = item.payload as Record<string, unknown>
    const tracerId = String(payload.tracer_id ?? '')
    if (!tracerId) return
    await addTracerStep(item.visit_id, item.survey_id, tracerId, {
      order: Number(payload.order ?? 0),
      observation: String(payload.observation ?? ''),
      standard_ref: String(payload.standard_ref ?? ''),
      result: (payload.result as 'pass' | 'fail' | 'na') ?? 'na',
      photo_ids: Array.isArray(payload.photo_ids) ? payload.photo_ids : [],
      interview_notes: typeof payload.interview_notes === 'string' ? payload.interview_notes : null,
      audio_transcript: typeof payload.audio_transcript === 'string' ? payload.audio_transcript : null,
    })
  },
}

// ── Sync engine ─────────────────────────────────────────────────────────────

class SurveySyncEngine {
  private isSyncing = false
  private rerunRequested = false
  private pollTimer: ReturnType<typeof setInterval> | null = null

  /** Start listening for connectivity and polling. */
  start(): void {
    if (this.pollTimer) return
    this.sync()
    window.addEventListener('online', () => this.sync())
    this.pollTimer = setInterval(() => {
      if (navigator.onLine) this.sync()
    }, POLL_INTERVAL_MS)
  }

  stop(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    window.removeEventListener('online', () => this.sync())
  }

  /** Process all queued items. */
  async sync(): Promise<{ processed: number; failed: number }> {
    // F-04 / #1864: refuse survey offline replay until AuthProvider binds offline owner
    const { offlineStore } = await import('../offline/store')
    if (!offlineStore.getActiveOwner()) {
      return { processed: 0, failed: 0 }
    }
    if (!navigator.onLine) return { processed: 0, failed: 0 }
    if (this.isSyncing) {
      this.rerunRequested = true
      return { processed: 0, failed: 0 }
    }
    this.isSyncing = true

    let processed = 0
    let failed = 0

    try {
      const allItems = await surveyStore.getAllQueued()
      // Skip items that have exceeded max retries — they wait for manual retry
      const items = allItems.filter((i) => i.retry_count < MAX_RETRIES)
      if (items.length === 0) return { processed: 0, failed: 0 }

      safeLogger.logEvent('survey_sync_started', { status: items.length })

      for (const item of items) {
        const processor = PROCESSORS[item.action]
        if (!processor) {
          await surveyStore.dequeue(item.id)
          continue
        }

        try {
          await processor(item)
          await surveyStore.dequeue(item.id)
          processed++
        } catch (err) {
          // Conflict = already synced, safe to discard
          if (err instanceof ApiRequestError && (err.status === 409 || err.code === 'conflict')) {
            await surveyStore.dequeue(item.id)
            processed++
            continue
          }

          // Terminal errors — blob is unrecoverable, mark and skip forever
          if (err instanceof NonRetryableSyncError) {
            await surveyStore.updateQueueItem(item.id, {
              last_error: err.message,
              retry_count: MAX_RETRIES,
              terminal: true,
            })
            failed++
            continue
          }

          // Auth errors should not be retried
          if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
            await surveyStore.updateQueueItem(item.id, {
              last_error: err.message,
              retry_count: MAX_RETRIES, // prevent further retries
            })
            failed++
            continue
          }

          // Transient errors — bump retry count but never delete
          const nextRetry = item.retry_count + 1
          await surveyStore.updateQueueItem(item.id, {
            retry_count: nextRetry,
            last_error: err instanceof Error ? err.message : 'Unknown error',
          })
          if (nextRetry >= MAX_RETRIES) {
            safeLogger.logError('survey_sync_item_max_retries', item.id)
          }
          failed++
        }
      }

      safeLogger.logEvent('survey_sync_completed', { status: processed })
    } finally {
      this.isSyncing = false
    }

    if (this.rerunRequested) {
      this.rerunRequested = false
      const rerun = await this.sync()
      return {
        processed: processed + rerun.processed,
        failed: failed + rerun.failed,
      }
    }

    return { processed, failed }
  }

  /** Number of pending items (for SyncStatus component). */
  async pendingCount(): Promise<number> {
    const items = await surveyStore.getAllQueued()
    return items.filter((i) => i.retry_count < MAX_RETRIES).length
  }

  /** Number of retryable items that failed after max retries (excludes terminal). */
  async failedCount(): Promise<number> {
    const items = await surveyStore.getAllQueued()
    return items.filter((i) => i.retry_count >= MAX_RETRIES && !i.terminal).length
  }

  /** Number of terminal (unrecoverable) items — require recapture, not retry. */
  async terminalFailedCount(): Promise<number> {
    const items = await surveyStore.getAllQueued()
    return items.filter((i) => i.terminal === true).length
  }

  /** Reset retry counts on all retryable failed items and re-sync. Terminal items are excluded. */
  async retryAll(): Promise<{ processed: number; failed: number }> {
    const items = await surveyStore.getAllQueued()
    const failed = items.filter((i) => i.retry_count >= MAX_RETRIES && !i.terminal)
    for (const item of failed) {
      await surveyStore.updateQueueItem(item.id, { retry_count: 0, last_error: null })
    }
    return this.sync()
  }
}

export const surveySyncEngine = new SurveySyncEngine()
